import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FiChevronDown, FiChevronRight, FiClock } from "react-icons/fi";
import Skeleton from "react-loading-skeleton";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { fetchDocumentAuditLogs } from "newui/features/Company/company-api";
import { companyDocumentKeys } from "../companyDocumentKeys";
import type { DocumentAuditLog, AuditChange } from "newui/features/Company/types/DocumentAuditLog.type";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";

dayjs.extend(relativeTime);

interface ChangeHistoryProps {
	companyId: number;
	documentId: number;
}

const ACTION_LABELS: Record<string, string> = {
	created: "Document created",
	updated: "Document updated",
	confirmed: "Insurance verification confirmed",
	file_replaced: "File replaced",
};

const FIELD_LABELS: Record<string, string> = {
	name: "Name",
	category_id: "Category",
	status: "Status",
	description: "Description",
	display: "Visibility",
	storage_path: "File",
	"extra_info.expiry_date": "Expiry Date",
	"extra_info.value": "Value",
	"extra_info.insurance.insurer_name": "Insurer",
	"extra_info.insurance.insured_name": "Insured",
	"extra_info.insurance.policy_number": "Policy Number",
	"extra_info.insurance.expiry_date": "Expiry Date (verified)",
	"extra_info.insurance.value": "Value (verified)",
	"extra_info.insurance.textract_inferred_sub_category": "Insurance Type",
	expiry_date: "Expiry Date",
	value: "Value",
	insurer_name: "Insurer",
	insured_name: "Insured",
	policy_number: "Policy Number",
	sub_category_id: "Sub Category",
};

const formatFieldName = (field: string): string => {
	return FIELD_LABELS[field] ?? field.replace(/_/g, " ").replace(/^extra_info\./, "");
};

const formatValue = (val: string | number | null | undefined): string => {
	if (val === null || val === undefined || val === "") {
		return "—";
	}
	return String(val);
};

const ChangeItem: React.FC<{ change: AuditChange }> = ({ change }) => (
	<div className="flex items-baseline gap-1 text-xs text-gray-600">
		<span className="font-medium text-gray-700">{formatFieldName(change.field)}:</span>
		{change.from !== null && change.from !== undefined ? (
			<>
				<span className="text-red-500 line-through">{formatValue(change.from)}</span>
				<span className="text-gray-400">→</span>
				<span className="text-green-600">{formatValue(change.to)}</span>
			</>
		) : (
			<span className="text-green-600">{formatValue(change.to)}</span>
		)}
	</div>
);

const AuditLogEntry: React.FC<{ log: DocumentAuditLog }> = ({ log }) => {
	const userName = `${log.user?.first_name ?? ""} ${log.user?.last_name ?? ""}`.trim() || "System";
	const timeAgo = dayjs(log.created_at).fromNow();
	const dateStr = dayjs(log.created_at).format("D MMM YYYY, h:mm A");
	const changes = log.metadata?.changes ?? [];
	const overrides = log.metadata?.overrides;

	return (
		<div className="border-b border-gray-100 py-2.5 last:border-b-0">
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1">
					<p className="text-xs font-medium text-gray-800">
						{ACTION_LABELS[log.action] ?? log.action}
					</p>
					<p className="mt-0.5 text-[11px] text-gray-400" title={dateStr}>
						{userName} · {timeAgo}
					</p>
				</div>
			</div>

			{changes.length > 0 && (
				<div className="mt-1.5 space-y-0.5 rounded bg-gray-50 px-2 py-1.5">
					{changes
						.filter((c) => !c.field.startsWith("extra_info.insurance.override_reasons"))
						.filter((c) => !c.field.startsWith("extra_info.insurance.feature_version"))
						.map((change, idx) => (
							<ChangeItem key={idx} change={change} />
						))}
				</div>
			)}

			{overrides && Object.keys(overrides).length > 0 && (
				<div className="mt-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5">
					<p className="text-[11px] font-medium text-amber-700">Override reasons:</p>
					{Object.entries(overrides).map(([field, reason]) => (
						<p key={field} className="text-[11px] text-amber-600">
							<span className="font-medium capitalize">{field.replace(/_/g, " ")}:</span>{" "}
							{reason}
						</p>
					))}
				</div>
			)}
		</div>
	);
};

export const ChangeHistory: React.FC<ChangeHistoryProps> = ({
	companyId,
	documentId,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const { data: logs, isLoading } = useQuery({
		queryKey: companyDocumentKeys.auditLogs(companyId, documentId),
		queryFn: () => fetchDocumentAuditLogs(companyId, documentId),
		enabled: isOpen,
	});

	const count = logs?.length ?? 0;

	return (
		<div className="mt-2 rounded-lg border border-gray-200">
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
			>
				<div className="flex items-center gap-2">
					<FiClock size={14} className="text-gray-400" />
					<span>Change History</span>
					{count > 0 && (
						<span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500">
							{count}
						</span>
					)}
				</div>
				{isOpen ? (
					<FiChevronDown size={16} className="text-gray-400" />
				) : (
					<FiChevronRight size={16} className="text-gray-400" />
				)}
			</button>

			{isOpen && (
				<div className="max-h-64 overflow-y-auto border-t border-gray-200 px-3">
					{isLoading ? (
						<LoadingSpinner fontSize="sm" spinnerSize="sm" />
					) : !logs || logs.length === 0 ? (
						<p className="py-3 text-center text-xs text-gray-400">
							No changes recorded
						</p>
					) : (
						logs.map((log) => <AuditLogEntry key={log.id} log={log} />)
					)}
				</div>
			)}
		</div>
	);
};
